const CUSTOM_TAB_PREFIX = "custom-";

/**
 * Tab id for a saved Operating Points view (unique per AppUserDisplaySettingsId).
 * @param {number|string} backendId
 * @returns {string}
 */
export function getCustomOperatingPointsTabId(backendId) {
  return `${CUSTOM_TAB_PREFIX}${backendId}`;
}

/**
 * @param {string} tabId
 * @returns {boolean}
 */
export function isCustomOperatingPointsTabId(tabId) {
  return typeof tabId === "string" && tabId.startsWith(CUSTOM_TAB_PREFIX);
}

/**
 * Backend id from `custom-{id}` tabs. Returns null for legacy `custom-{viewName}` ids.
 * @param {string} tabId
 * @returns {number|null}
 */
export function getBackendIdFromCustomOperatingPointsTabId(tabId) {
  if (!isCustomOperatingPointsTabId(tabId)) return null;
  const suffix = tabId.slice(CUSTOM_TAB_PREFIX.length);
  if (/^\d+$/.test(suffix)) {
    const id = parseInt(suffix, 10);
    return Number.isFinite(id) && id > 0 ? id : null;
  }
  return null;
}

/**
 * When multiple tabs share a label, append the settings id so users can tell them apart.
 * @param {Array<{ id: string, label: string }>} tabs
 * @returns {Array<{ id: string, label: string }>}
 */
export function disambiguateDuplicateOperatingPointsTabLabels(tabs) {
  const countByLabel = new Map();
  for (const tab of tabs) {
    countByLabel.set(tab.label, (countByLabel.get(tab.label) || 0) + 1);
  }
  return tabs.map((tab) => {
    if ((countByLabel.get(tab.label) || 0) <= 1) return tab;
    const backendId = getBackendIdFromCustomOperatingPointsTabId(tab.id);
    return backendId != null
      ? { ...tab, label: `${tab.label} (${backendId})` }
      : tab;
  });
}

/**
 * @param {Array<{ id: string, label: string }>} tabs
 * @returns {Array<{ id: string, label: string }>}
 */
/** Strip disambiguation suffix e.g. "My View (151)" → "My View" for API names. */
export function getOperatingPointsSettingsNameFromTabLabel(label) {
  return String(label ?? "").replace(/\s\(\d+\)$/, "");
}

export function dedupeOperatingPointsTabsById(tabs) {
  const seen = new Set();
  return tabs.filter((tab) => {
    if (seen.has(tab.id)) return false;
    seen.add(tab.id);
    return true;
  });
}
